import { styled } from "goober";
import { useLayoutEffect, useRef, useState } from "react";
import { Tooltip } from "./Tooltip";

const Outer = styled("div")`
  display: flex;
`;

const Item = styled("div")`
  position: relative;
  display: flex;
  align-items: center;
`;

const OverflowOuter = styled("div")`
  border-radius: 50%;
  background-color: var(--cell-bg, var(--surface-primary));
`;

const OverflowBadge = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--alpha4);
  color: var(--text-secondary);
  width: var(--size);
  height: var(--size);
  font-size: 9px;
  font-weight: 500;
  line-height: 12px;
  transition: background-color 0.1s ease-out;

  &:hover {
    background-color: var(--alpha8);
  }
`;

type Props = {
  // A `bare` item carries no backdrop-coloured ring of its own, so in cutout
  // mode the item beneath it is carved to make a real transparent gap. A
  // non-bare item (e.g. a present avatar with a painted ring) separates itself,
  // so the item beneath it is left whole.
  items: {
    node: React.ReactNode;
    id: string;
    label?: string;
    bare?: boolean;
  }[];
  itemSize: number;
  compressFraction?: number;
  reverse?: boolean;
  vertical?: boolean;
  maxItems?: number;
  totalCount?: number;
  outlineWidth?: number;
  // Carve the overlap gap out of items as a real transparent cutout instead of
  // painting a ring in the backdrop colour, so the negative space shows the true
  // background (and survives hover/colour changes). Only items stacked under a
  // `bare` neighbour are carved.
  cutout?: boolean;
  // Cutout gap width in px (default 2).
  gap?: number;
  // Measure the available width and roll overflow into the "+N" badge to fit,
  // for stacks in width-constrained table columns. Only ever shows fewer items
  // than `maxItems`, never more.
  collapseToFit?: boolean;
  // In `collapseToFit` mode, which edge the pile hugs within its column.
  align?: "start" | "end";
};

const COMPRESS_FRACTION = 6;

// Painted width of n overlapping slots is itemSize + (n-1)*step, because the
// leading Outer margin is cancelled by the first item's equal negative margin.
export function facePileCapacity(
  availableWidth: number,
  itemSize: number,
  compressFraction = COMPRESS_FRACTION
): number {
  const step = (itemSize * (compressFraction - 1)) / compressFraction;
  return Math.max(0, 1 + Math.floor((availableWidth - itemSize) / step));
}

export function FacePile({
  items,
  itemSize,
  compressFraction = COMPRESS_FRACTION,
  reverse = false,
  vertical = false,
  maxItems = 5,
  totalCount,
  outlineWidth = 1,
  cutout = false,
  gap = 2,
  collapseToFit = false,
  align = "start",
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [capacity, setCapacity] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!collapseToFit) return;
    const root = rootRef.current;
    if (!root) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries.find((entry) => entry.target === root)?.contentRect
        .width;
      if (width === undefined) return;
      if (width === 0) return;
      const next = facePileCapacity(width, itemSize, compressFraction);
      setCapacity((current) => (current === next ? current : next));
    });
    observer.observe(root);
    return () => observer.disconnect();
  }, [collapseToFit, itemSize, compressFraction]);

  const total = totalCount ?? items.length;
  const effectiveMaxItems =
    collapseToFit && capacity !== null
      ? Math.max(1, Math.min(capacity, maxItems, total))
      : maxItems;
  const hasOverflow = total > effectiveMaxItems;
  const visibleItems = hasOverflow
    ? items.slice(0, effectiveMaxItems - 1)
    : items;
  if (hasOverflow) {
    const tooltipContent = items
      .slice(effectiveMaxItems - 1)
      .map((item) => item.label)
      .filter(Boolean)
      .join(", ");

    const badge = (
      <OverflowOuter
        style={{
          background: cutout ? "transparent" : undefined,
          outline: cutout
            ? undefined
            : `${outlineWidth}px solid var(--cell-bg, var(--surface-primary))`,
        }}
      >
        <OverflowBadge
          style={{ "--size": `${itemSize}px` } as React.CSSProperties}
        >
          +{total - visibleItems.length}
        </OverflowBadge>
      </OverflowOuter>
    );

    visibleItems.push({
      id: "overflow",
      node: tooltipContent ? (
        <Tooltip content={tooltipContent}>{badge}</Tooltip>
      ) : (
        badge
      ),
      bare: cutout,
    });
  }

  const overlap = itemSize / compressFraction;
  const cutoutRadius = itemSize / 2 + gap;
  const padding = cutout ? outlineWidth : 0;
  const cutoutCenter = `calc(50% ${reverse ? "-" : "+"} ${itemSize - overlap}px)`;
  const cutoutMask = cutout
    ? `radial-gradient(circle at ${vertical ? `50% ${cutoutCenter}` : `${cutoutCenter} 50%`}, rgba(255,255,255,0) ${cutoutRadius}px, rgba(255,255,255,1) ${cutoutRadius}px)`
    : undefined;

  return (
    <div
      ref={collapseToFit ? rootRef : undefined}
      style={
        collapseToFit
          ? {
              display: "flex",
              width: "100%",
              minWidth: 0,
              justifyContent: align === "end" ? "flex-end" : "flex-start",
            }
          : { display: "contents" }
      }
    >
      <Outer
        style={{
          marginLeft: vertical ? 0 : overlap,
          marginTop: vertical ? overlap : 0,
          flexDirection: vertical
            ? reverse
              ? "column-reverse"
              : "column"
            : reverse
              ? "row-reverse"
              : "row",
        }}
      >
        {visibleItems.map(({ node, id }, index) => {
          // A painted neighbour supplies its own separation; a bare one needs
          // a cutout so translucent content cannot reveal the item underneath.
          const overlapped = cutout && visibleItems[index + 1]?.bare === true;
          return (
            <Item
              style={{
                // Keep presence rings inside the mask as presence arrives after mount.
                padding,
                marginTop: vertical ? -overlap - padding : -padding,
                marginRight: -padding,
                marginBottom: -padding,
                marginLeft: vertical ? -padding : -overlap - padding,
                zIndex: index + 1,
                ...(overlapped && {
                  maskImage: cutoutMask,
                  WebkitMaskImage: cutoutMask,
                }),
              }}
              key={id}
            >
              {node}
            </Item>
          );
        })}
      </Outer>
    </div>
  );
}
